import React, { useEffect, useRef } from 'react';
import CardUsageOptionsForm from 'features/cards/components/card-usage-options-form/card-usage-options-form';

import './card-usage-options-modal.scss';
import { useModal } from '@ampol/design-system';
import { ButtonColor } from '@ampol/design-system/lib';
import { useDispatch, useSelector } from 'react-redux';
import { getCardOptionsReset } from 'actions/storeActions';
import Env from 'Env';

interface CardUsageOptionsModalProps {
  /** Array of account numbers selected in the data table action menu. */
  cardAccountsList: string[];
  /** Array of card numbers selected in the data table action menu. */
  cardsList: string[];
  cardsData?: any[];
  onClose: () => void;
  onEvReissueComplete?: () => void;
}

const CardUsageOptionsModal = ({
  cardAccountsList,
  onClose,
  cardsList,
  cardsData,
  onEvReissueComplete,
}: CardUsageOptionsModalProps) => {
  const dispatch = useDispatch();
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;
  const closeFormModalRef = useRef<(() => void) | null>(null);
  // When true, formModal.close() skips getCardOptionsReset() so the saga can
  // complete naturally. cards.tsx watches isLoading and shows the acknowledge modal.
  const evReissuePendingRef = useRef(false);

  const setCardOptionsLoading = useSelector((state: any) => state.cardUsageOptions.isLoading);
  const primaryActionButton = useSelector((state: any) => state.modalStore.primaryActionButton);
  const secondaryActionButton = useSelector((state: any) => state.modalStore.secondaryActionButton);

  const getModalActions = () => [
    ...(primaryActionButton
      ? [{ ...primaryActionButton, inactive: primaryActionButton.inactive || setCardOptionsLoading }]
      : []),
    ...(secondaryActionButton
      ? [
          {
            ...secondaryActionButton,
            onClick: secondaryActionButton.onClick || onClose,
            inactive: secondaryActionButton.inactive || setCardOptionsLoading,
          },
        ]
      : []),
  ];

  const handleClose = () => {
    // Skip the store reset when closing for EV reissue — the saga is still running
    // and cards.tsx needs isLoading to remain true until the saga completes.
    if (!evReissuePendingRef.current) {
      dispatch(getCardOptionsReset());
    }
    onClose();
  };

  const formId = 'card-usage-options-modal';

  const formModal = useModal({
    id: 'card-usage-options-modal',
    title: 'Update Card Usage',
    subtitle: '',
    content: (
      <CardUsageOptionsForm
        formId={formId}
        contractNumber={cardAccountsList}
        cardsList={cardsList}
        cardsData={cardsData}
        onClose={handleClose}
        onEvReissueComplete={() => {
          if (!isEvWay4Enabled) {
            return;
          }
          evReissuePendingRef.current = true;
          // Signal cards.tsx to start watching isLoading for saga completion.
          onEvReissueComplete?.();
          closeFormModalRef.current?.(); // triggers handleClose → skips getCardOptionsReset → calls onClose()
        }}
      />
    ),
    variant: 'triple',
    color: ButtonColor.Blue,
    actions: getModalActions() as any,
    formId: primaryActionButton?.formId,
    onClose: handleClose,
  });

  closeFormModalRef.current = () => formModal.close();

  useEffect(() => {
    formModal.open();
  }, []);

  return null;
};

export default CardUsageOptionsModal;
